import { isValid } from '@/common/utils/validation/common.utils';
import { Vehicle } from '@/core/domain/branches/models/branch.model';
import { GtmAddToCart } from '@/core/domain/gtm/models/gtm-add-to-cart.model';
import { GtmBeginCheckout } from '@/core/domain/gtm/models/gtm-begin-checkout.model';
import { GtmPurchase } from '@/core/domain/gtm/models/gtm-purchase.model';
import { GtmRefund } from '@/core/domain/gtm/models/gtm-refund-model';
import {
    GtmReservationComplete,
    GtmReservationCompleteEvent,
    ExtraItem,
} from '@/core/domain/gtm/models/gtm-reservation-complete.model';
import { GtmReserveSearch } from '@/core/domain/gtm/models/gtm-reserve-search.model';
import { GtmSelectCart } from '@/core/domain/gtm/models/gtm-select-car-model';
import { GtmSelectExtras } from '@/core/domain/gtm/models/gtm-select-extras-model';
import { GtmSelectItem } from '@/core/domain/gtm/models/gtm-select-item.model';
import { GtmViewItemList } from '@/core/domain/gtm/models/gtm-view-item-list-model';
import { GtmRepository } from '@/core/domain/gtm/repositories/gtm.repository';
import { GetSummaryUseCase } from '@/core/domain/summary/usecases/getSummary.usecase';
import { updateReservation } from '@/core/reservation/store/reservation.actions';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { selectVehicles } from '@/core/reservation/store/vehicles.selector';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import _ from 'lodash';
import {
    catchError,
    first,
    forkJoin,
    from,
    Observable,
    of,
    switchMap,
} from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class GtmImplRepository extends GtmRepository {
    constructor(
        private readonly gtmService: GoogleTagManagerService,
        private readonly store: Store,
        private readonly getSummaryUseCase: GetSummaryUseCase,
    ) {
        super();
    }

    override refund(reservationCode: string): Observable<boolean> {
        const dataLayer: GtmRefund = {
            event: 'refund',
            ecommerce: {
                transaction_id: reservationCode,
            },
        };
        return this.setDataLayer({ ecommerce: null }).pipe(
            switchMap(() => {
                return this.setDataLayer(dataLayer);
            }),
        );
    }

    override purchase(reservationCode: string): Observable<boolean> {
        const dataLayer: GtmPurchase = {
            event: 'purchase',
            ecommerce: {
                transaction_id: reservationCode,
                currency: '', // 'MXN';
                value: 0, // 1500.0;
                coupon: '', // 'AGO24';
                payment_type: 'Pago en oficina',
                items: [],
            },
        };
        let locationName: string;
        let differentLocationName: string;
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservationResponse) => {
                locationName = _.get(reservationResponse, 'location.name', '');
                differentLocationName = _.get(
                    reservationResponse,
                    'differentLocation.name',
                    '',
                );
                dataLayer.ecommerce.currency = _.get(
                    reservationResponse,
                    'vehicle.charges.' +
                        _.get(reservationResponse, 'flowType', 'office') +
                        '.currency',
                    '',
                );
                dataLayer.ecommerce.coupon = _.get(
                    reservationResponse,
                    'discountCode',
                    '',
                );
                dataLayer.ecommerce.value = _.get(
                    reservationResponse,
                    'total',
                    0,
                );
                return forkJoin([
                    of(reservationResponse),
                    of(
                        this.buildItemDataSelectItem(
                            reservationResponse,
                            locationName,
                            differentLocationName,
                        ),
                    ),
                    this.getSummaryUseCase.execute(),
                ]);
            }),
            switchMap(([reservationResponse, itemData, summaryResponse]) => {
                this.store.dispatch(
                    updateReservation({
                        value: {
                            total: summaryResponse.data?.totalAmount,
                        },
                    }),
                );
                itemData.booking_days = _.get(
                    summaryResponse,
                    'data.rentDays',
                    0,
                );
                dataLayer.ecommerce.payment_type = _.get(
                    summaryResponse,
                    'data.isPrePaid',
                    false,
                )
                    ? 'BBVA'
                    : 'Pago en oficina';
                dataLayer.ecommerce.items.push(itemData);
                const itemDataInsurances = {
                    item_id: _.toString(
                        _.get(reservationResponse, 'insurances.0.id', ''),
                    ),
                    item_name: _.get(
                        reservationResponse,
                        'insurances.0.name',
                        '',
                    ),
                    list_name: _.get(
                        reservationResponse,
                        'insurances.0.title',
                        '',
                    ),
                    price: _.get(reservationResponse, 'insurances.0.price', 0),
                    coupon: _.get(reservationResponse, 'discountCode', ''),
                    quantity: 1,
                    index: 2,
                };
                dataLayer.ecommerce.items.push(itemDataInsurances);
                _.forEach(
                    _.get(reservationResponse, 'products', []),
                    (item, index) => {
                        dataLayer.ecommerce.items.push({
                            item_id: _.toString(_.get(item, 'id', '')),
                            item_name: _.get(item, 'name', ''),
                            list_name: _.get(item, 'title', ''),
                            price: _.get(item, 'price', 0),
                            coupon: _.get(
                                reservationResponse,
                                'discountCode',
                                '',
                            ),
                            quantity: 1,
                            index: 3 + _.toNumber(index),
                        });
                    },
                );
                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    override beginCheckout(): Observable<boolean> {
        const dataLayer: GtmBeginCheckout = {
            event: 'begin_checkout',
            ecommerce: {
                destino_buscado: '',
                currency: '', // 'MXN';
                value: 0, // 1500.0;
                coupon: '', // 'AGO24';
                items: [],
            },
        };
        let locationName: string;
        let differentLocationName: string;
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservationResponse) => {
                locationName = _.get(reservationResponse, 'location.name', '');
                differentLocationName = _.get(
                    reservationResponse,
                    'differentLocation.name',
                    '',
                );
                dataLayer.ecommerce.destino_buscado = locationName;
                dataLayer.ecommerce.currency = _.get(
                    reservationResponse,
                    'vehicle.charges.' +
                        _.get(reservationResponse, 'flowType', 'office') +
                        '.currency',
                    '',
                );
                dataLayer.ecommerce.coupon = _.get(
                    reservationResponse,
                    'discountCode',
                    '',
                );
                dataLayer.ecommerce.value = _.get(
                    reservationResponse,
                    'total',
                    0,
                );
                return forkJoin([
                    of(reservationResponse),
                    of(
                        this.buildItemDataSelectItem(
                            reservationResponse,
                            locationName,
                            differentLocationName,
                        ),
                    ),
                    this.getSummaryUseCase.execute(),
                ]);
            }),
            switchMap(([reservationResponse, itemData, summaryResponse]) => {
                itemData.booking_days = _.get(
                    summaryResponse,
                    'data.rentDays',
                    0,
                );
                dataLayer.ecommerce.items.push(itemData);
                const itemDataInsurances = {
                    item_id: _.toString(
                        _.get(reservationResponse, 'insurances.0.id', ''),
                    ),
                    item_name: _.get(
                        reservationResponse,
                        'insurances.0.name',
                        '',
                    ),
                    list_name: _.get(
                        reservationResponse,
                        'insurances.0.title',
                        '',
                    ),
                    price: _.get(reservationResponse, 'insurances.0.price', 0),
                    coupon: _.get(reservationResponse, 'discountCode', ''),
                    quantity: 1,
                    index: 2,
                };
                dataLayer.ecommerce.items.push(itemDataInsurances);
                _.forEach(
                    _.get(reservationResponse, 'products', []),
                    (item, index) => {
                        dataLayer.ecommerce.items.push({
                            item_id: _.toString(_.get(item, 'id', '')),
                            item_name: _.get(item, 'name', ''),
                            list_name: _.get(item, 'title', ''),
                            price: _.get(item, 'price', 0),
                            coupon: _.get(
                                reservationResponse,
                                'discountCode',
                                '',
                            ),
                            quantity: 1,
                            index: 3 + _.toNumber(index),
                        });
                    },
                );
                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    override addToCart(): Observable<boolean> {
        const dataLayer: GtmAddToCart = {
            event: 'add_to_cart',
            ecommerce: {
                destino_buscado: '',
                currency: '', // 'MXN';
                value: 0, // 1500.0;
                coupon: '', // 'AGO24';
                items: [],
            },
        };
        let locationName: string;
        let differentLocationName: string;
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservationResponse) => {
                locationName = _.get(reservationResponse, 'location.name', '');
                differentLocationName = _.get(
                    reservationResponse,
                    'differentLocation.name',
                    '',
                );
                dataLayer.ecommerce.destino_buscado = locationName;
                dataLayer.ecommerce.currency = _.get(
                    reservationResponse,
                    'vehicle.charges.' +
                        _.get(reservationResponse, 'flowType', 'office') +
                        '.currency',
                    '',
                );
                dataLayer.ecommerce.coupon = _.get(
                    reservationResponse,
                    'discountCode',
                    '',
                );
                dataLayer.ecommerce.value = _.get(
                    reservationResponse,
                    'total',
                    0,
                );
                return forkJoin([
                    of(
                        this.buildItemDataSelectItem(
                            reservationResponse,
                            locationName,
                            differentLocationName,
                        ),
                    ),
                    this.getSummaryUseCase.execute(),
                ]);
            }),
            switchMap(([itemData, summaryResponse]) => {
                itemData.booking_days = _.get(
                    summaryResponse,
                    'data.rentDays',
                    0,
                );
                dataLayer.ecommerce.items.push(itemData);
                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    override selectItem(): Observable<boolean> {
        const dataLayer: GtmSelectItem = {
            event: 'select_item',
            ecommerce: {
                destino_buscado: '',
                item_list_name: '',
                items: [],
            },
        };
        let locationName: string;
        let differentLocationName: string;
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservationResponse) => {
                locationName = _.get(reservationResponse, 'location.name', '');
                differentLocationName = _.get(
                    reservationResponse,
                    'differentLocation.name',
                    '',
                );
                dataLayer.ecommerce.destino_buscado = locationName;
                dataLayer.ecommerce.item_list_name = locationName;
                return forkJoin([
                    of(
                        this.buildItemDataSelectItem(
                            reservationResponse,
                            locationName,
                            differentLocationName,
                        ),
                    ),
                    this.getSummaryUseCase.execute(),
                ]);
            }),
            switchMap(([itemData, summaryResponse]) => {
                itemData.booking_days = _.get(
                    summaryResponse,
                    'data.rentDays',
                    0,
                );
                dataLayer.ecommerce.items.push(itemData);
                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    private buildItemDataSelectItem(
        reservationResponse: unknown,
        locationName: string,
        differentLocationName: string,
    ) {
        return {
            item_id: _.toString(_.get(reservationResponse, 'vehicle.id', '')), // '72';
            item_name: _.get(reservationResponse, 'vehicle.name', ''), // 'Chevrolet Beat';
            brand: _.get(reservationResponse, 'vehicle.model', ''), // 'Chevrolet Beat';
            codigo_auto: _.get(reservationResponse, 'vehicle.code', ''), // 'MCAR';
            price: _.toNumber(
                _.get(reservationResponse, 'vehicle.charges.office.amount', 0),
            ), // 837.5;
            coupon: _.get(reservationResponse, 'discountCode', ''), // 'AGO24';
            item_category: _.get(reservationResponse, 'vehicle.model', ''), // 'Mini automático';
            item_list_name: locationName, // 'Destino buscado';
            index: 1, // 1;
            quantity: 1, // 1;
            pick_up_location: locationName, // 'Destino buscado';
            pick_up_time: _.get(reservationResponse, 'startHour', ''), // '8:30';
            drop_off_location: isValid(differentLocationName)
                ? differentLocationName
                : locationName, // 'Destino entrega';
            drop_off_time: _.get(reservationResponse, 'endHour', ''), // '9:30';
            booking_days: 0, // 4;
            tipo_reserva: (_.get(reservationResponse, 'flowType', 'office') ===
            'office'
                ? 'en destino'
                : 'Pago en línea') as 'Pago en línea' | 'en destino',
        };
    }

    override viewItemList(): Observable<boolean> {
        const dataLayer: GtmViewItemList = {
            event: 'view_item_list',
            ecommerce: {
                destino_buscado: '',
                item_list_name: '',
                items: [],
            },
        };
        let locationName: string;
        let differentLocationName: string;
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservationResponse) => {
                locationName = _.get(reservationResponse, 'location.name', '');
                differentLocationName = _.get(
                    reservationResponse,
                    'differentLocation.name',
                    '',
                );
                dataLayer.ecommerce.destino_buscado = locationName;
                dataLayer.ecommerce.item_list_name = locationName;
                return forkJoin([
                    of(reservationResponse),
                    this.store.select(selectVehicles).pipe(first()),
                ]);
            }),
            switchMap(([reservationResponse, vehiclesResponse]) => {
                const vehicles = _.get(
                    vehiclesResponse,
                    'vehicles',
                    [],
                ) as Vehicle[];
                _.forEach(vehicles, (item, index) => {
                    dataLayer.ecommerce.items.push({
                        item_id: _.toString(item.id), // '72';
                        item_name: item.name, // 'Chevrolet Beat';
                        brand: item.model, // 'Chevrolet Beat';
                        codigo_auto: item.code, // 'MCAR';
                        price: _.get(item, 'charges.prepaid.amount', 0), // 837.5;
                        coupon: _.get(reservationResponse, 'discountCode', ''), // 'AGO24';
                        item_category: item.model, // 'Mini automático';
                        item_list_name: locationName, // 'Destino buscado';
                        index: index + 1, // 1;
                        quantity: 1, // 1;
                        pick_up_location: locationName, // 'Destino buscado';
                        pick_up_time: _.get(
                            reservationResponse,
                            'startHour',
                            '',
                        ), // '8:30';
                        drop_off_location: isValid(differentLocationName)
                            ? differentLocationName
                            : locationName, // 'Destino entrega';
                        drop_off_time: _.get(
                            reservationResponse,
                            'endHour',
                            '',
                        ), // '9:30';
                    });
                });
                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    override reserveSearch(): Observable<boolean> {
        const dataLayer: GtmReserveSearch = {
            event: 'reserve_search',
            coupon: '',
            destino_buscado: '',
        };
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservation) => {
                dataLayer.destino_buscado = _.get(
                    reservation,
                    'location.name',
                    '',
                );
                dataLayer.coupon = _.get(reservation, 'discountCode', '');
                return this.setDataLayer(dataLayer);
            }),
        );
    }

    override setDataLayer<T extends object>(dataLayer: T): Observable<boolean> {
        return from(this.gtmService.pushTag(dataLayer)).pipe(
            catchError(() => {
                return of(false);
            }),
            switchMap(() => {
                return of(true);
            }),
        );
    }

    override selectCart(): Observable<boolean> {
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservation) => {
                const [ecommerce, vehicle] =
                    this.mapReservationToEcommerceData(reservation);

                const item = {
                    item_name: vehicle.model,
                    item_id: vehicle.code,
                    item_list_name: vehicle.name,
                    item_category: vehicle.name,
                    price:
                        ecommerce.reservationType === 'prepaid'
                            ? vehicle.charges.prepaid.rentRate
                            : vehicle.charges.office.rentRate,
                    quantity: 1,
                    item_brand: vehicle.model,
                    location_id: ecommerce?.pickUpLocationCode,
                };

                ecommerce.items = [item];
                const dataLayer: GtmSelectCart = {
                    event: 'select_car',
                    ecommerce: ecommerce,
                };

                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    override selectExtras(): Observable<boolean> {
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservation) => {
                const [ecommerce, , insurances, products, origin] =
                    this.mapReservationToEcommerceData(reservation);

                ecommerce.items = this.buildExtrasItems(
                    insurances,
                    products,
                    origin,
                    ecommerce.reservationType,
                );

                const dataLayer: GtmSelectExtras = {
                    event: 'select_extras',
                    ecommerce,
                };

                return this.setDataLayer({ ecommerce: null }).pipe(
                    switchMap(() => {
                        return this.setDataLayer(dataLayer);
                    }),
                );
            }),
        );
    }

    override reservationComplete(id: string): Observable<boolean> {
        return this.store.select(selectReservation).pipe(
            first(),
            switchMap((reservation) => {
                const [ecommerce, vehicle, insurances, products, origin] =
                    this.mapReservationToEcommerceData(reservation);
                ecommerce.contractId = id;
                ecommerce.reservationNumber = id;

                const items: any[] = [];
                items.push({
                    item_name: vehicle.model,
                    item_id: vehicle.code,
                    item_list_name: vehicle.name,
                    item_category: vehicle.name,
                    price:
                        ecommerce.reservationType === 'prepaid'
                            ? vehicle.charges.prepaid.rentRate
                            : vehicle.charges.office.rentRate,
                    quantity: 1,
                    item_brand: vehicle.model,
                    location_id: ecommerce?.pickUpLocationCode,
                });

                items.push(
                    ...this.buildExtrasItems(
                        insurances,
                        products,
                        origin,
                        ecommerce.reservationType,
                    ),
                );
                ecommerce.items = items;

                const paymentEvent: GtmReservationCompleteEvent =
                    ecommerce.reservationType === 'prepaid'
                        ? 'reservation_complete_online'
                        : 'reservation_complete_office';

                return this.pushReservationComplete(
                    'reservation_complete',
                    ecommerce,
                ).pipe(
                    switchMap(() => {
                        return this.pushReservationComplete(
                            paymentEvent,
                            ecommerce,
                        );
                    }),
                );
            }),
        );
    }

    private pushReservationComplete(
        event: GtmReservationCompleteEvent,
        ecommerce: GtmReservationComplete['ecommerce'],
    ): Observable<boolean> {
        const dataLayer: GtmReservationComplete = { event, ecommerce };
        return this.setDataLayer({ ecommerce: null }).pipe(
            switchMap(() => {
                return this.setDataLayer(dataLayer);
            }),
        );
    }

    private mapReservationToEcommerceData(reservation: any) {
        const {
            total,
            currency,
            location,
            differentLocation,
            startDate,
            endDate,
            origin,
            flowType,
            vehicle,
            insurances,
            products,
        } = reservation;

        const pickUp = new Date(startDate);
        const dropOff = new Date(endDate);
        const lengthOfRental = Math.ceil(
            (dropOff.getTime() - pickUp.getTime()) / (1000 * 60 * 60 * 24),
        );
        const today = new Date();

        const leadTime = Math.max(
            0,
            Math.ceil(
                (pickUp.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
            ),
        );
        const ecommerce = {
            value: total,
            currency,
            pickUpLocationCode: location?.locationCode ?? '',
            dropOffLocationCode:
                differentLocation?.locationCode ?? location?.locationCode ?? '',
            pickUpLocation: location?.name ?? '',
            dropOffLocation: differentLocation?.name ?? location?.name ?? '',
            pickUpDate: pickUp.toISOString(),
            dropOffDate: dropOff.toISOString(),
            lengthOfRental: lengthOfRental,
            leadTime: leadTime,
            contractId: '',
            customerType: 'Invitado',
            payment: flowType === 'prepaid' ? 'BBVA' : 'Pago en Oficina',
            country: origin === 'NAT' ? 'Nacional' : 'Internacional',
            reservationType: flowType,
            items: [],
        };
        return [ecommerce, vehicle, insurances, products, origin];
    }

    private buildExtrasItems(
        insurances: any[],
        products: any[],
        origin: string,
        reservationType: string,
    ): ExtraItem[] {
        const items: ExtraItem[] = [];

        for (const insurance of insurances ?? []) {
            let price;
            if (origin === 'INT') {
                price = insurance.price;
            } else if (reservationType === 'prepaid') {
                price = insurance.prePaid?.priceDiscount ?? insurance.price;
            } else {
                price = insurance.prePaid?.priceDiscount ?? insurance.price;
            }

            items.push({
                item_category: 'extras',
                item_category2: 'coverage',
                item_category3: insurance?.tittle ?? '',
                item_name: insurance?.name ?? '',
                price,
                quantity: 1,
            });
        }

        for (const product of products ?? []) {
            items.push({
                item_category: 'extras',
                item_category2: 'equipment',
                item_category3: product?.name ?? '',
                item_name: product?.name ?? '',
                price: product.price,
                quantity: 1,
            });
        }

        return items;
    }
}
